import classNames from 'classnames';
import { useDebugDispatch, useDebugState } from '../DebugProvider';
import {
    ExEmptyState,
    ExIcon,
    ExCheckbox,
    CheckboxSize,
    ExIconButton,
    IconButtonType,
    IconButtonSize,
} from '@boomi/exosphere';
import ComponentWithTooltip from '../../../generic/tooltip/ComponentWithTooltip';

const CallStack = () => {
    const { callStack, isDebuggerAttached, callStackItemsArePinned } = useDebugState();
    const dispatch = useDebugDispatch();

    if (!isDebuggerAttached) {
        return (
            <div className="full-height flex align-center">
                <div className="center">
                    <ExEmptyState
                        label="The debugger is not connected"
                        text="Enter a state ID to connect to a running state"
                    >
                        <ExIcon icon="Offline" slot="header" />
                    </ExEmptyState>
                </div>
            </div>
        );
    }

    if (isDebuggerAttached && !callStack.length) {
        return (
            <div className="full-height flex align-center">
                <div className="center callstack-waiting">Waiting...</div>
            </div>
        );
    }

    return (
        <>
            <div className="callstack-header flex align-center">Call Stack</div>
            <div className="full-height call-stack-outer" data-testid="call-stack">
                <div className="call-stack-inner">
                    {callStack.map((callStackItem) => {
                        const rootFaultCount = Object.keys(callStackItem.rootFaults ?? {}).length;
                        const breakpointHitCount = callStackItem.breakpointsHit?.length ?? 0;
                        const classes = classNames({
                            'call-stack-item': true,
                            'is-selected': callStackItem.isSelected,
                        });

                        return (
                            <button
                                onClick={() =>
                                    dispatch({
                                        type: 'setSelectedCallStackItem',
                                        stackItemId: callStackItem.id,
                                    })
                                }
                                className={classes}
                                key={callStackItem.id}
                                type="button"
                            >
                                <span>{callStackItem.developerName}</span>
                                {rootFaultCount > 0 && (
                                    <ComponentWithTooltip
                                        trigger={['hover', 'focus']}
                                        fadeTime={0.1}
                                        tooltipContent={`${rootFaultCount} root fault${
                                            rootFaultCount === 1 ? '' : 's'
                                        }`}
                                        tooltipPlacement="right"
                                        clearance={10}
                                        arrowClearance={7}
                                    >
                                        <ExIcon
                                            className="root-faults-found"
                                            icon="status-fail"
                                            label={`${rootFaultCount} root fault${
                                                rootFaultCount === 1 ? '' : 's'
                                            }`}
                                        />
                                    </ComponentWithTooltip>
                                )}
                                {breakpointHitCount > 0 && (
                                    <ComponentWithTooltip
                                        trigger={['hover', 'focus']}
                                        fadeTime={0.1}
                                        tooltipContent={`${breakpointHitCount} assertion${
                                            breakpointHitCount === 1 ? '' : 's'
                                        } hit`}
                                        tooltipPlacement="right"
                                        clearance={10}
                                        arrowClearance={7}
                                    >
                                        <ExIcon
                                            className="break-points-hit"
                                            icon="status-success"
                                            label={`${breakpointHitCount} assertion${
                                                breakpointHitCount === 1 ? '' : 's'
                                            } hit`}
                                        />
                                    </ComponentWithTooltip>
                                )}
                                <span className="flex-child-right">
                                    {callStackItem.dateTimeExecuted}
                                </span>
                            </button>
                        );
                    })}
                </div>
            </div>
            <span className="call-stack-footer flex align-center">
                <ExCheckbox
                    size={CheckboxSize.SMALL}
                    checked={callStackItemsArePinned}
                    onChange={({ target }: CustomEvent) => {
                        dispatch({
                            type: 'setCallStackItemsPinned',
                            pinned: (target as HTMLInputElement).checked,
                        });
                    }}
                >
                    Pin selected items
                </ExCheckbox>
                <div className="flex-child-right">
                    <ComponentWithTooltip
                        trigger={['hover', 'focus']}
                        fadeTime={0.1}
                        tooltipContent="Clear call stack"
                        tooltipPlacement="right"
                        clearance={10}
                        arrowClearance={7}
                    >
                        <ExIconButton
                            data-testid="clear-callstack"
                            circular
                            hideBrowserTooltip
                            onClick={() => dispatch({ type: 'clearCallStack' })}
                            icon="Disabled"
                            type={IconButtonType.SECONDARY}
                            size={IconButtonSize.SMALL}
                        />
                    </ComponentWithTooltip>
                </div>
            </span>
        </>
    );
};

export default CallStack;
